import { useEffect, useState, useRef } from "react";
import { api, fmtErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, X, Wind } from "lucide-react";
import { toast } from "sonner";

/**
 * Exit-intent popup. Fires once per session when the mouse leaves the top of
 * the viewport (only on desktop). Silently no-ops on:
 *   - already-dismissed sessions (sessionStorage flag)
 *   - already-authenticated users (they don't need a waitlist opt-in)
 *   - the /admin/*, /host/*, /login, /register, /payment/* routes
 *
 * Captures email into the existing /api/waitlist endpoint.
 */
export default function ExitIntentPopup() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    // Don't run on internal / auth pages
    const p = window.location.pathname;
    const skipPaths = ["/login", "/register", "/host", "/admin", "/payment", "/favorites"];
    if (skipPaths.some((s) => p.startsWith(s))) return;

    // Don't run for signed-in users (heuristic: presence of access_token cookie)
    if (document.cookie.includes("access_token")) return;

    // Only run once per session
    if (sessionStorage.getItem("gs_exit_seen")) return;

    const onMouseOut = (e) => {
      // Only trigger when the mouse exits the top of the viewport
      if (e.clientY > 5 || e.relatedTarget || shownRef.current) return;
      shownRef.current = true;
      setOpen(true);
      sessionStorage.setItem("gs_exit_seen", "1");
    };
    document.addEventListener("mouseout", onMouseOut);
    return () => document.removeEventListener("mouseout", onMouseOut);
  }, []);

  const dismiss = () => setOpen(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSaving(true);
    try {
      await api.post("/waitlist", {
        name: "Exit-intent lead",
        email,
        company: "",
        portfolio_size: 0,
      });
      toast.success("You're on the list. Check your inbox for the freshness audit checklist.");
      setOpen(false);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A1128]/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-popup"
      onClick={dismiss}
    >
      <div
        className="relative bg-white rounded-3xl overflow-hidden max-w-lg w-full shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
          aria-label="Close"
          data-testid="exit-intent-close"
        >
          <X className="w-4 h-4 text-slate-600" />
        </button>

        <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-700 p-8 text-white">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Wind className="w-6 h-6 text-white" />
          </div>
          <h3 className="mt-6 font-serif text-3xl leading-tight">Wait — get the free Freshness Audit Checklist.</h3>
          <p className="mt-3 text-emerald-50 text-sm leading-relaxed">
            The exact 12-point checklist we use to certify a stay. Print it, use it before your next
            guest arrives. We&apos;ll email it in 60 seconds.
          </p>
        </div>

        <form onSubmit={submit} className="p-8 space-y-4">
          <Input
            type="email"
            required
            placeholder="you@yourbusiness.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-full h-12 px-5"
            data-testid="exit-intent-email"
          />
          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 rounded-full bg-[#0A1128] hover:bg-[#1A2A3A] text-white font-medium"
            data-testid="exit-intent-submit"
          >
            {saving ? "Sending…" : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Send me the checklist
              </>
            )}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">
            No spam. Unsubscribe with one click. See our <a href="/privacy" className="underline">privacy policy</a>.
          </p>
        </form>
      </div>
    </div>
  );
}
